import React, { useCallback, useEffect, useState } from "react";
import { Flex, Input, Button, Form, Typography, FormProps, Grid } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { apiDomain } from "configs/configs";
import styles from "pages/login/login.module.scss";
import { logout } from "helpers/helpers";
import clsx from "clsx";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import useUserStore from "stores/user";

type ComponentProps = {};

const { useBreakpoint } = Grid;

type FieldData = Parameters<Required<FormProps>["onFieldsChange"]>[0];

const Page: React.FC<ComponentProps> = () => {
  const screens = useBreakpoint();

  const isMobile = !screens.lg;
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isInProcess, setIsInProcess] = useState(false);
  const [isAuthorizationError, setIsAuthorizationError] = useState(false);
  const { setUser } = useUserStore((store) => store);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [form, values]);

  const handleFinish = useCallback(() => {
    const fetchData = async () => {
      const url = `${apiDomain}/login`;
      const options: RequestInit = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: values.phone
            .replace(/\D/g, "")
            .replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, "+7 $1 $2-$3-$4"),
          password: values.password,
        }),
        credentials: "include",
      };

      try {
        setIsInProcess(true);
        setIsAuthorizationError(false);

        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        const data = await response.json();

        setIsInProcess(false);

        //  Set user data to app state
        setUser(() => ({
          isAuthorized: true,
          isAuthorizationChecked: true,
          userData: data,
        }));

        //  Set user data to local storage
        localStorage.setItem("userData", JSON.stringify(data));
      } catch (error) {
        setIsInProcess(false);
        setIsAuthorizationError(true);

        logout();
      }
    };

    fetchData();
  }, [values, setUser, setIsInProcess, setIsAuthorizationError]);

  const handlePhoneBlur = useCallback(() => {
    const phoneValue = form.getFieldValue("phone");
    if (!phoneValue) return;

    const digitsOnly = phoneValue.replace(/\D/g, "");
    if (digitsOnly.length !== 10) return;

    const formattedPhone = digitsOnly.replace(
      /(\d{3})(\d{3})(\d{2})(\d{2})/,
      "$1 $2-$3-$4"
    );
    form.setFieldsValue({ phone: formattedPhone });
  }, [form]);

  const handleFieldsChange: FormProps["onFieldsChange"] = useCallback(
    (changedFields: FieldData) => {
      setIsAuthorizationError(false);

      const phoneField = changedFields.find(
        (field) => field.name[0] === "phone"
      );

      if (!phoneField) return;

      const inputValue = phoneField.value ? phoneField.value : "";

      form.setFieldsValue({ phone: inputValue.replace(/[^\d\s-]/g, "") });
    },
    [form, setIsAuthorizationError]
  );

  return (
    <MobileInnerWrapper isCentered={true}>
      <div className={clsx(styles.container, !!isMobile && styles.isMobile)}>
        <Flex vertical gap={16}>
          <Form
            layout={"vertical"}
            form={form}
            onFinish={handleFinish}
            onFieldsChange={handleFieldsChange}
          >
            <Form.Item
              name="phone"
              label="Телефон"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите номер телефона!",
                },
              ]}
              validateTrigger={false}
              required={false}
            >
              <Input
                size="large"
                addonBefore="+7"
                name="login"
                onBlur={handlePhoneBlur}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Пароль"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите пароль!",
                },
              ]}
              validateTrigger={false}
              required={false}
            >
              <Input.Password
                size="large"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                size="large"
                className={styles.button}
                disabled={!isSubmittable}
                htmlType="submit"
                loading={isInProcess}
              >
                Войти
              </Button>
            </Form.Item>
            {isAuthorizationError && (
              <Typography.Paragraph type="danger">
                Не удалось выполнить авторизацию. Пожалуйста, проверьте свои
                данные и повторите попытку.
              </Typography.Paragraph>
            )}
            <Typography.Paragraph>
              Проблемы со входом?{" "}
              <Typography.Link>Написать сообщение</Typography.Link>
            </Typography.Paragraph>
          </Form>
        </Flex>
      </div>
    </MobileInnerWrapper>
  );
};

export default Page;
